import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  HtmlToPdfError,
  MAX_HTML_CHARS,
  MAX_HTML_FILE_MB,
  htmlPdfFilename,
  htmlToPdf,
  readHtmlFile,
} from './lib'

const UTILITY = getUtilityDefinition('htmlToPdf').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; blob: Blob; objectUrl: string; filename: string; durationMs: number }
  | { kind: 'error'; message: string; errorCode: string }

type SourceMode = 'paste' | 'file'

function localizedNumber(value: number): string {
  return value.toLocaleString()
}

function classifyError(error: unknown, t: Translations): { code: string; message: string } {
  if (error instanceof HtmlToPdfError) {
    switch (error.code) {
      case 'empty_html':
        return { code: error.code, message: t.htmlToPdf.emptyHtml }
      case 'too_large':
        return {
          code: error.code,
          message: format(t.htmlToPdf.tooLarge, {
            chars: localizedNumber(MAX_HTML_CHARS),
            size: MAX_HTML_FILE_MB,
          }),
        }
      case 'unsupported_file':
        return { code: error.code, message: t.htmlToPdf.unsupportedFile }
      case 'unsupported_browser':
        return { code: error.code, message: t.htmlToPdf.unsupportedBrowser }
      case 'conversion_error':
        return { code: error.code, message: t.htmlToPdf.conversionError }
    }
  }

  return { code: 'conversion_error', message: t.htmlToPdf.conversionError }
}

function sampleHtml(): string {
  return `<h1>Коммерческое предложение</h1>
<p>Этот HTML будет преобразован в PDF прямо в браузере.</p>
<h2>Пункты</h2>
<ul>
  <li>Заголовки, абзацы и списки сохраняются как печатная страница.</li>
  <li>CSS из style-атрибутов и встроенных тегов применяется перед рендером.</li>
</ul>`
}

export function HtmlVPdfForm() {
  const t = useT()
  const formatBytes = useFormatBytes()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const resultUrlRef = useRef<string | null>(null)
  const [mode, setMode] = useState<SourceMode>('paste')
  const [html, setHtml] = useState('')
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  useEffect(() => {
    return () => {
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    }
  }, [])

  function revokeResult() {
    if (resultUrlRef.current) {
      URL.revokeObjectURL(resultUrlRef.current)
      resultUrlRef.current = null
    }
  }

  function resetResult() {
    revokeResult()
    setPhase({ kind: 'idle' })
  }

  function setError(code: string, message: string) {
    setPhase({ kind: 'error', errorCode: code, message })
    toolError(UTILITY, code)
  }

  async function handleFile(file: File | null) {
    if (!file) return
    resetResult()
    try {
      const text = await readHtmlFile(file)
      setSourceFile(file)
      setHtml(text)
      setMode('file')
    } catch (error) {
      const classified = classifyError(error, t)
      setSourceFile(null)
      setError(classified.code, classified.message)
    }
  }

  function clearAll() {
    resetResult()
    setHtml('')
    setSourceFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function useSample() {
    resetResult()
    setMode('paste')
    setSourceFile(null)
    setHtml(sampleHtml())
  }

  async function handleSubmit() {
    if (phase.kind === 'loading') return

    resetResult()
    setPhase({ kind: 'loading' })
    const startMs = Date.now()
    const inputBytes = new Blob([html]).size
    toolInvoke(UTILITY, {
      source: mode,
      input_bytes: inputBytes,
      input_chars: html.length,
    })

    try {
      const blob = await htmlToPdf(html)
      const objectUrl = URL.createObjectURL(blob)
      const durationMs = Date.now() - startMs
      const filename = htmlPdfFilename(sourceFile?.name ?? null)
      resultUrlRef.current = objectUrl
      toolSuccess(UTILITY, durationMs, {
        source: mode,
        input_bytes: inputBytes,
        input_chars: html.length,
        output_bytes: blob.size,
      })
      setPhase({ kind: 'success', blob, objectUrl, filename, durationMs })
    } catch (error) {
      const classified = classifyError(error, t)
      toolError(UTILITY, classified.code, {
        source: mode,
        input_bytes: inputBytes,
        input_chars: html.length,
      })
      setPhase({ kind: 'error', errorCode: classified.code, message: classified.message })
    }
  }

  const disabled = phase.kind === 'loading'
  const inputBytes = new Blob([html]).size
  const overCharLimit = html.length > MAX_HTML_CHARS

  return (
    <Card className="mb-10">
      <CardContent className="space-y-6 pt-6">
        <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/30 p-1">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setMode('paste')}
            className={cn(
              'rounded-md px-3 py-2 text-sm font-medium transition-colors',
              mode === 'paste'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
              disabled && 'pointer-events-none opacity-60',
            )}
          >
            {t.htmlToPdf.pasteMode}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'rounded-md px-3 py-2 text-sm font-medium transition-colors',
              mode === 'file'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
              disabled && 'pointer-events-none opacity-60',
            )}
          >
            {t.htmlToPdf.fileMode}
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".html,.htm,text/html,application/xhtml+xml"
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            void handleFile(event.target.files?.[0] ?? null)
            event.currentTarget.value = ''
          }}
        />

        {sourceFile && (
          <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
              HTML
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{sourceFile.name}</p>
              <p className="text-xs text-muted-foreground">{formatBytes(sourceFile.size)}</p>
            </div>
            <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={clearAll}>
              {t.common.deleteFile}
            </Button>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="html-to-pdf-input" className="text-sm font-medium text-foreground">
            {mode === 'file' ? t.htmlToPdf.fileTextareaLabel : t.htmlToPdf.textareaLabel}
          </label>
          <textarea
            id="html-to-pdf-input"
            value={html}
            disabled={disabled}
            rows={14}
            spellCheck={false}
            placeholder={t.htmlToPdf.placeholder}
            onChange={(event) => {
              resetResult()
              setHtml(event.target.value)
              if (mode === 'file') setSourceFile(null)
            }}
            className={cn(
              'min-h-[22rem] w-full resize-y rounded-lg border bg-background p-4',
              'font-mono text-sm leading-6 text-foreground outline-none transition-colors',
              'placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20',
              disabled && 'opacity-60',
              overCharLimit && 'border-destructive focus:border-destructive focus:ring-destructive/20',
            )}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {format(t.htmlToPdf.inputStats, {
                chars: localizedNumber(html.length),
                bytes: formatBytes(inputBytes),
              })}
            </span>
            <span>
              {format(t.htmlToPdf.limit, {
                chars: localizedNumber(MAX_HTML_CHARS),
                size: MAX_HTML_FILE_MB,
              })}
            </span>
          </div>
        </div>

        <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          {t.htmlToPdf.localNotice}
        </div>

        {phase.kind === 'error' && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
            <p className="font-medium text-destructive">{t.common.errorTitle}</p>
            <p className="mt-1 text-sm text-destructive/90">{phase.message}</p>
          </div>
        )}

        {phase.kind === 'success' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-950 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
            <p className="font-medium">{t.common.done}</p>
            <p className="mt-1 text-sm">
              {format(t.htmlToPdf.successDetails, { size: formatBytes(phase.blob.size) })}
            </p>
            <Button asChild className="mt-4">
              <a href={phase.objectUrl} download={phase.filename}>
                {t.htmlToPdf.downloadPdf}
              </a>
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            disabled={disabled || html.trim().length === 0 || overCharLimit}
            onClick={handleSubmit}
          >
            {disabled ? t.htmlToPdf.processing : t.htmlToPdf.convert}
          </Button>
          <Button type="button" variant="outline" disabled={disabled} onClick={useSample}>
            {t.htmlToPdf.useSample}
          </Button>
          {(html.length > 0 || sourceFile) && (
            <Button type="button" variant="ghost" disabled={disabled} onClick={clearAll}>
              {t.htmlToPdf.clear}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
